import React from "react";
import { Stepper, Step, StepLabel, StepConnector, stepConnectorClasses, styled, StepIconProps, Stack, Typography, Icon, Chip } from '@mui/material';
import { useSelector } from 'react-redux';
import { CopyToClipboard } from '../common/CopyToClipBoard';
import { ContentCopy, Check} from '@mui/icons-material';
import SegmentChart from "../common/SegmentChart";
import { CutterHistory } from "../types/cutterHistory";
import CloudIcon from '../images/ConnectorCloudStep.svg';
import { getColorFromPercentage } from '../utils/colorUtils';
import { RootState } from '../store';
import { grey } from "@mui/material/colors";

interface StepDiagnosticProps {
    cutter: CutterHistory;
}

interface StepInfo {
    title: string;
    description: string;
}

// Dictionary to keep step index, title, and description in sync
const stepDescriptions: Record<number, StepInfo> = {
    0: {
        title: 'Cutter',
        description: 'Machine sends ZIP report from customer site'
    },
    1: {
        title: 'cutter-report.lectra.com',
        description: 'Web server that validates the ZIP file and extracts its content (GLUE)'
    },
    2: {
        title: 'Transform (Talend)',
        description: 'Talend transforms data to prepare Oracle Database insertions'
    },
    3: {
        title: 'Load (Oracle)',
        description: 'Oracle database stores processed data'
    },
    4: {
        title: 'LISA',
        description: 'ERP system makes data available for user interfaces and reporting'
    }
};

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.active}, &.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: theme.palette.secondary.main,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#eaeaf0',
        borderTopWidth: 3,
        borderRadius: 1,
        ...theme.applyStyles('dark', {
            borderColor: theme.palette.grey[800],
        }),
    },
}));


const ColorlibStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean } }>(
    ({ theme, ownerState }) => ({
        borderRadius: '50%',
        backgroundColor: ownerState.active
            ? (ownerState.completed ? theme.palette.secondary.main : theme.palette.warning.main)
            : ownerState.completed
            ? theme.palette.secondary.main
            : theme.palette.grey[300],
        color: 'transparent',
    })
);

const ULStyle = styled('ul')({
    listStylePosition: 'inside',
    paddingLeft: '0.2rem'
});

interface ColorlibStepIconProps extends StepIconProps {
    cutter: CutterHistory;
}



const chartSx = { color: 'transparent', marginTop: '-0.5rem', width: '2.5rem', height: '2.5rem', marginX: '1rem' };

const ColorlibStepIcon: React.FC<ColorlibStepIconProps> = ({ active, completed, className, icon, cutter }) => {
    const softwareUpdateIndex = cutter.state?.stats_SoftwareUpdateIndex ?? cutter.statistics.softwareUpdateIndex;
    const connectionRate = cutter.state?.stats_ConnectionRate ?? cutter.statistics.connectionRate;
    if (icon === 1) {
        return (
            <SegmentChart
                sx={chartSx}
                percent={softwareUpdateIndex}
                title={<p>Software update index: {(softwareUpdateIndex / 100).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 })}</p>}
                description={
                    <ULStyle>
                        <li>Measures how recent software versions are for {cutter.serialNumber} compared to all other {cutter.state?.harD_Model ?? 'cutter'} cutters.</li>
                        <li>Returns a weighted percentage (0-100) where 100 means all versions are the most recent available.</li>
                        <li>This score uses weighted coefficients according to the {cutter.state?.harD_Model ?? ''} series.</li>
                    </ULStyle>
                }
            />
        );
    }
    
    if (icon === 2) {
        return (
            <SegmentChart
                sx={chartSx}
                percent={connectionRate}
                title={<p>Connection rate: {(connectionRate / 100).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 })}</p>}
                description={
                    <>
                        <p>Connection Rate (0-100%) measures the connectivity of the cutter over the last 365 days based on three key indicators:</p>
                        <ULStyle>
                            <li>1. Zip Report integrations in ERP LISA</li>
                            <li>2. Dashboard event upload frequency</li>
                            <li>3. Daily ZIP Report uploads (availability)</li>
                        </ULStyle>
                    </>
                }
            />
        );
    }

    return (
        <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
            <Check sx={{ color: "#fff" }} />
        </ColorlibStepIconRoot>
    );
};

const StepDiagnostic: React.FC<StepDiagnosticProps> = ({ cutter }) => {
const softwareUpdateIndex = cutter.state?.stats_SoftwareUpdateIndex ?? cutter.statistics.softwareUpdateIndex;
const connectionRate = cutter.state?.stats_ConnectionRate ?? cutter.statistics.connectionRate;
const isHelpVisible = useSelector((state: RootState) => state.persistedHelpVisibilityReducer.isVisible);

const getStepLabel = (index: number): React.ReactNode => {
    return (
        <Stack direction="column" spacing={0} alignItems="center">
            <Stack direction="row" spacing={0} alignItems="center">
                <Typography variant="body2" fontWeight={index === 0 ? "bold" : "medium"}>{index === 0 ? cutter.serialNumber : stepDescriptions[index].title}</Typography>
                {index === 0 && (
                    <CopyToClipboard
                        TextToCopy={cutter.serialNumber}
                        title='Copy serial to clipboard'
                        AckMessage={`Serial '${cutter.serialNumber}' copied to clipboard`}>
                        <ContentCopy sx={{ color: 'default', padding: 0, fontSize: '0.6rem' }} titleAccess="Copy SerialNumber" />
                    </CopyToClipboard>
                    )}
            </Stack>
            {index === 0 && cutter.state.sofT_Commercial_Version && (
                <Chip label={cutter.state.sofT_Commercial_Version} 
                    sx={{
                        backgroundColor: getColorFromPercentage(softwareUpdateIndex),
                        color: (theme) => theme.palette.mode === 'light' ? 'inherit' : '#252525',
                        paddingX:2, paddingY:1, height: '12px', marginTop:'-4em', fontSize: '0.65rem', fontWeight: 'bold'
                    }}
                        size="small" />
                        )}
            {isHelpVisible && (
                <Typography variant="caption" color="info" sx={{ fontSize: '0.65rem', textAlign: 'center', maxWidth: '120px', 
                        marginTop: index === 0 ? '2em' : '0'
                }}>
                    {stepDescriptions[index].description}
                </Typography>
            )}
        </Stack>
    );
};

    // Get ETL step from diagnostics or default to 4 (completed)
    const etlStep = (cutter.state?.diagnostics?.failures
        && cutter.state.diagnostics.failures.length > 0)
        ? cutter.state.diagnostics.failures[0].etlStep 
        : 4;

    return (
        <Stepper
            activeStep={etlStep}
            connector={<ColorlibConnector />}
            alternativeLabel
            sx={{
                zIndex: 100,
                display: 'contents',
                '& .MuiStep-root:nth-of-type(2) .MuiStepConnector-root': {
                    [`& .${stepConnectorClasses.line}`]: {
                        position: 'relative',
                        width:'80%',
                        marginLeft: '10%',
                        borderColor: getColorFromPercentage(connectionRate),
                        '&::before': {
                            content: '""',
                            zIndex: 1,
                            marginX: '2px',
                            backgroundColor: getColorFromPercentage(connectionRate),
                            position: 'absolute',
                            top: '40%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '40px',
                            height: '40px',
                            maskImage: `url(${CloudIcon})`,
                            maskRepeat: 'no-repeat',
                            maskPosition: 'center',
                            maskSize: 'contain',
                            WebkitMaskImage: `url(${CloudIcon})`,
                            WebkitMaskRepeat: 'no-repeat',
                            WebkitMaskPosition: 'center',
                            WebkitMaskSize: 'contain',
                            boxShadow: '0 0 0 8px gray',
                        }
                    },
                    [`&.${stepConnectorClasses.active}, &.${stepConnectorClasses.completed}`]: {
                        [`& .${stepConnectorClasses.line}::before`]: {
                            borderColor: getColorFromPercentage(connectionRate),
                            backgroundColor: getColorFromPercentage(connectionRate)
                        }
                    }
                }
            }}>
            {Object.keys(stepDescriptions).map((key) => {
                const index = parseInt(key);
                return (
                    <Step key={index} completed={index < etlStep || etlStep === 4}>
                        <StepLabel slots={{ stepIcon: (props) => <ColorlibStepIcon {...props} cutter={cutter} /> }}
                            sx={{ fontSize: '0.4rem', fontWeight: index === 0 ? 'bold' : 'normal' }}>
                            {getStepLabel(index)}
                        </StepLabel>
                    </Step>
                );
            })}
        </Stepper>
    );
}

export default StepDiagnostic;